import { SpacerValueOrKey } from "@faire/web/slate/spacing/getSpacing";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import cn from "classnames";
import React from "react";

import { getSpacing } from "../spacing";
import { isResponsiveProp, ResponsiveProp } from "../Theme/ResponsiveProp";

import * as styles from "./styles.css";

export interface IFlexProps
  extends Omit<React.AllHTMLAttributes<HTMLElement>, "as" | "size"> {
  flex?:
    | styles.Sprinkles["flex"]
    | `${number} ${number}${"px" | "%" | ""} ${
        | `${number}${"px" | "%" | ""}`
        | "auto"}`;
  align?: styles.Sprinkles["alignItems"];
  justify?: styles.Sprinkles["justifyContent"];
  direction?: styles.Sprinkles["flexDirection"];
  gap?: ResponsiveProp<SpacerValueOrKey>;
  children?: React.ReactNode | undefined;
  // @types/styled-components uses dark magic to try to infer the correct type for `as`. As far as I can tell,
  // it doesn't work, instead opening up prop types to "any" in many cases.
  as?: any;
}

export const Flex = React.forwardRef<HTMLDivElement, IFlexProps>(
  (
    {
      align: alignProp,
      direction: directionProp,
      justify: justifyProp,
      gap: gapProp,
      flex: flexProp,
      style: styleProp,
      className: classNameProp,
      as: asProp,
      children,
      ...rest
    },
    ref
  ) => {
    let gap: styles.Sprinkles["gap"] | undefined = undefined;
    const vars: Record<string, string | undefined> = {};

    if (gapProp) {
      if (isResponsiveProp(gapProp)) {
        vars[styles.flexGapVar] = getSpacing(gapProp.mobileAndAbove);
        vars[styles.flexTabletGapVar] =
          gapProp.tabletAndAbove != null
            ? getSpacing(gapProp.tabletAndAbove)
            : undefined;
        vars[styles.flexDesktopGapVar] =
          gapProp.desktopAndAbove != null
            ? getSpacing(gapProp.desktopAndAbove)
            : undefined;
        vars[styles.flexXLargeGapVar] =
          gapProp.xLargeAndAbove != null
            ? getSpacing(gapProp.xLargeAndAbove)
            : undefined;
        vars[styles.flexXXLargeGapVar] =
          gapProp.xxLargeDesktop != null
            ? getSpacing(gapProp.xxLargeDesktop)
            : undefined;
        gap = {
          mobileAndAbove: styles.flexGapVar,
          tabletAndAbove:
            gapProp.tabletAndAbove != null
              ? styles.flexTabletGapVar
              : undefined,
          desktopAndAbove:
            gapProp.desktopAndAbove != null
              ? styles.flexDesktopGapVar
              : undefined,
          xLargeAndAbove:
            gapProp.xLargeAndAbove != null
              ? styles.flexXLargeGapVar
              : undefined,
          xxLargeDesktop:
            gapProp.xxLargeDesktop != null
              ? styles.flexXXLargeGapVar
              : undefined,
        };
      } else {
        gap = styles.flexGapVar;
        vars[styles.flexGapVar] = getSpacing(gapProp);
      }
    }
    let flex: styles.Sprinkles["flex"] | undefined = undefined;
    let flexGrow: styles.Sprinkles["flexGrow"] | undefined = undefined;
    let flexShrink: styles.Sprinkles["flexShrink"] | undefined = undefined;
    let flexBasis: styles.Sprinkles["flexBasis"] | undefined = undefined;
    if (flexProp && typeof flexProp === "string" && /^\d/.test(flexProp)) {
      const parts = flexProp.split(" ");
      vars[styles.flexGrowVar] = parts[0];
      vars[styles.flexShrinkVar] = parts[1] ?? "1";
      vars[styles.flexBasisVar] = parts[2] ?? "0%";
      flexGrow = styles.flexGrowVar;
      flexShrink = styles.flexShrinkVar;
      flexBasis = styles.flexBasisVar;
    } else {
      flex = flexProp as styles.Sprinkles["flex"] | undefined;
    }

    return React.createElement(
      asProp || "div",
      {
        ...rest,
        ref: ref,
        style: { ...assignInlineVars(vars), ...styleProp },
        className: cn(
          classNameProp,
          styles.displayFlex,
          styles.sprinkles({
            gap,
            flex,
            flexGrow,
            flexShrink,
            flexBasis,
            alignItems: alignProp,
            flexDirection: directionProp,
            justifyContent: justifyProp,
          })
        ),
      },
      children
    );
  }
);
